/*
 * eslint-disable @typescript-eslint/naming-convention
 *
 * Postgres 컬럼은 snake_case 이고 supabase-js 는 컬럼명을 그대로 키로 쓴다.
 * 이 파일은 그 경계(행 → 도메인 타입)라 snake_case 가 불가피하다.
 */
/* eslint-disable @typescript-eslint/naming-convention */
import { getAuthedClient } from '~/common/utils/supabase.util.ts';
import { getMyProfileId } from '~/features/profile/api/profile.api';

import type { CreateOrderInput, Order, OrderStatus } from '~/features/payment/types/payment.type';

interface OrderRow {
  id: string;
  class_id: string;
  class_title: string;
  amount: number;
  status: OrderStatus;
  tutor_id: string | null;
  payer_name: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  created_at: string;
}

function toOrder(row: OrderRow): Order {
  return {
    id: row.id,
    classId: row.class_id,
    tutorId: row.tutor_id ?? undefined,
    payerName: row.payer_name ?? undefined,
    classTitle: row.class_title,
    amount: row.amount,
    status: row.status,
    paidAt: row.paid_at ?? undefined,
    cancelledAt: row.cancelled_at ?? undefined,
    createdAt: row.created_at,
  };
}

export async function fetchOrdersApi(): Promise<Order[]> {
  const client = await getAuthedClient();
  if (!client) return [];

  const { data, error } = await client
    .from('orders')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return ((data as OrderRow[] | null) ?? []).map(toOrder);
}

/**
 * 주문 생성 (계좌이체).
 * @description 카드 결제가 막혀 있어 결제창 호출이 없다. 주문은 `pending`(입금 대기)으로 만들어지고
 *  입금 확인은 운영자가 수동으로 한다 — 화면 문구도 "결제 완료"가 아니라 "입금 대기"로 유지해야 한다.
 *
 *  현금영수증은 주문이 만들어진 **뒤에** 접수한다(주문 없이 떠도는 요청을 만들지 않기 위해).
 *  접수 실패는 주문을 되돌리지 않고 호출부가 안내로 처리한다.
 */
/**
 * 정산 조회 (지도사) — 나에게 귀속된 주문.
 * @description RLS 의 `orders_tutor_read` 가 `tutor_id = 내 프로필` 인 행만 통과시킨다.
 *  주문자 개인정보는 가져오지 않는다 — 정산에 필요한 건 금액·상태·클래스뿐이다.
 */
export async function fetchSettlementApi(): Promise<Order[]> {
  const client = await getAuthedClient();
  if (!client) return [];
  const profileId = await getMyProfileId();
  if (!profileId) return [];

  const { data, error } = await client
    .from('orders')
    .select('*')
    .eq('tutor_id', profileId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return ((data as OrderRow[] | null) ?? []).map(toOrder);
}

export async function createOrderApi(
  input: CreateOrderInput,
): Promise<{ order: Order; receiptSaved: boolean }> {
  const client = await getAuthedClient();
  if (!client) throw new Error('로그인이 필요합니다.');

  const profileId = await getMyProfileId();
  if (!profileId) throw new Error('먼저 프로필을 등록해주세요.');

  const { data, error } = await client
    .from('orders')
    .insert({
      profile_id: profileId,
      class_id: input.classId,
      class_title: input.classTitle,
      amount: input.amount,
      tutor_id: input.tutorId ?? null,
      payer_name: input.payerName?.trim() || null,
    })
    .select('*')
    .single();

  if (error) throw error;
  const order = toOrder(data as OrderRow);

  let receiptSaved = true;
  if (input.receiptPhone) {
    const { error: receiptError } = await client.from('receipt_requests').insert({
      profile_id: profileId,
      order_id: order.id,
      class_id: input.classId,
      amount: input.amount,
      phone: input.receiptPhone,
    });
    receiptSaved = !receiptError;
  }

  return { order, receiptSaved };
}

/** 환불 요청 — 상태만 바꾼다. 실제 환불은 운영자가 계좌로 처리한다. */
export async function requestRefundApi(orderId: string): Promise<Order> {
  const client = await getAuthedClient();
  if (!client) throw new Error('로그인이 필요합니다.');

  const { data, error } = await client
    .from('orders')
    .update({ status: 'refund_requested' })
    .eq('id', orderId)
    .select('*')
    .single();

  if (error) throw error;
  return toOrder(data as OrderRow);
}

/** 주문 취소 — 입금 전(`pending`)에만 의미가 있다. */
export async function cancelOrderApi(orderId: string): Promise<Order> {
  const client = await getAuthedClient();
  if (!client) throw new Error('로그인이 필요합니다.');

  const { data, error } = await client
    .from('orders')
    .update({ status: 'cancelled', cancelled_at: new Date().toISOString() })
    .eq('id', orderId)
    .select('*')
    .single();

  if (error) throw error;
  return toOrder(data as OrderRow);
}
